import { useState, useEffect } from "react";
import CustomText, { textStyles } from "../../components/CustomText";
import { Box, useMediaQuery } from "@mui/material";
import CustomButton from "../../components/CustomButton";
import { useTheme } from "@mui/material/styles";
import industry1 from "../../assets/industry1.webp";
import industry2 from "../../assets/industry2.webp";
import industry5 from "../../assets/industry5.webp";
import industry6 from "../../assets/industry6.webp";
import IndustryCard from "../../components/cards/IndustryCard";
import topline1 from "../../assets/topline1.png";
import topline2 from "../../assets/topline2.png";
import { useTranslation } from "react-i18next";
import { BASE_URL } from "../../components/BASE_URL";

// Skeleton loader
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const IndustrySection = () => {
  const { t, i18n } = useTranslation();
    const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const [expandedIndex, setExpandedIndex] = useState(0);

  // ✅ Fixed local images
  const cardImages = [industry1, industry2, industry5, industry5, industry6];

  // 🔹 API State
  const [industryData, setIndustryData] = useState({
    title: "",
    description: "",
    list: [],
  });
  const [loading, setLoading] = useState(true);

 useEffect(() => {
  async function loadIndustry() {
    try {
      const res = await fetch(
        `${BASE_URL}content/sections?page=1&limit=10&sort=order&include=items&search=Home_Industry_Section`
      );
      const json = await res.json();

      const section = (json?.data || []).find(
        (s) => s?.name?.trim().toLowerCase() === "home_industry_section"
      );

      if (section?.items?.length) {
        const selectedItem =
          i18n.language === "es"
            ? section.items.find((item) => item.idx === 1)
            : section.items.find((item) => item.idx === 0);

        const parsedList = Array.isArray(selectedItem?.list)
          ? selectedItem.list
          : selectedItem?.list
            ? JSON.parse(selectedItem.list)
            : [];

        setIndustryData({
          title: selectedItem?.title || "",
          description: selectedItem?.description || "",
          list: parsedList,
        });
      }
    } catch (err) {
      console.error("Error loading industry section:", err);
    } finally {
      setLoading(false);
    }
  }

  loadIndustry();
}, [i18n.language]);


  const getTruncatedDescription = (desc, index) => {
    const words = desc.split(" ");
    if (words.length <= 7 || index === expandedIndex) return desc;
    return words.slice(0, 7).join(" ") + "...";
  };

  return (
    <Box
      sx={{
        position: "relative",
        px: isMobile ? 2 : 13,
        py: 8,
        background: `url(${topline1}) top left no-repeat, 
                     url(${topline2}) top left 20px no-repeat, 
                     url(${topline1}) bottom right no-repeat,
                     url(${topline2}) bottom right 20px no-repeat`,
        backgroundSize: "30% 30%, 30% 30%, 30% 30%, 30% 30%",
        backgroundRepeat: "no-repeat, no-repeat, no-repeat, no-repeat",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {/* 🔹 Title */}
        <CustomText
          style={{
            ...textStyles.h3,
            marginBottom: "1rem",
            marginTop: "1rem",
            color: "#072533",
            maxWidth: "800px",
            margin: "0 auto",
            fontSize: isMobile ? "30px" : "48px",
          }}
        >
          {loading ? <Skeleton width={400} height={40} /> : industryData.title}
        </CustomText>

        {/* 🔹 Description */}
        <CustomText
          style={{
            ...textStyles.body2,
            color: "#868282",
            maxWidth: "1105px",
            textAlign: "center",
            margin: "0 auto",
            marginTop: "30px",
          }}
        >
          {loading ? (
            <Skeleton width={600} height={20} count={2} />
          ) : (
            industryData.description
          )}
        </CustomText>

        {/* 🔹 Cards */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 7 : 4,
            mt: 8,
          }}
        >
          <Box
            sx={{
              flex: 7,
              display: "flex",
              flexDirection: "column",
              gap: 3,
              pr: 1,
            }}
          >
            {loading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} height={100} />
                ))
              : industryData.list.map((industry, index) => (
                  <IndustryCard
                    key={index}
                    title={industry.name}
                    description={getTruncatedDescription(
                      industry.value,
                      index
                    )}
                    isActive={expandedIndex === index}
                    onClick={() => {
                      setExpandedIndex((prev) =>
                        prev === index ? null : index
                      );
                    }}
                    sx={{
                      border: "1px solid #D1D3D9",
                      backgroundColor: "white",
                      "&:hover": {
                        border: "2px solid #003149",
                      },
                    }}
                  />
                ))}
          </Box>

          {/* 🔹 Image on the right */}
          <Box
            sx={{
              flex: 3,
              display: { xs: "none", lg: "flex" },
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "20px",
              overflow: "hidden",
              height: "430px",
              backgroundColor: "#f4f4f4",
            }}
          >
            {loading ? (
              <Skeleton width="100%" height="100%" />
            ) : (
              <Box
                component="img"
                src={cardImages[expandedIndex] || industry1}
                alt="Industry visual"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default IndustrySection;





